/**
 * OverviewTab – the school at a glance: headline figures, what needs a
 * decision or a look, and how each class is doing.
 *
 * Every figure comes from lib/classroom/attention.ts and lib/analytics/metrics.ts;
 * this only lays them out. Each "needs attention" item links to the place it
 * is fixed: one teacher or class opens its drawer, several open the list.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, CheckCircle2, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { MetricCard, MetricGrid } from '../analytics/MetricCard';
import { AnalyticsErrorState, AnalyticsNote, AnalyticsPanel, ChartSkeleton, TableSkeleton, AnalyticsEmptyState } from '../analytics/AnalyticsPanel';
import { DataTable, type Column } from '../analytics/DataTable';
import { DateRangeFilter, makeRange, type RangeValue } from '../analytics/DateRangeFilter';
import { ComparisonChart } from '../analytics/charts';
import { formatPercent, inRange, previousRange, scoreTone, summarise, type Result } from '../../lib/analytics/metrics';
import { NEAR_FULL_AT, QUIET_DAYS, classActivity, needsAttention, type AttentionItem } from '../../lib/classroom/attention';
import type { SchoolRoster } from '../../hooks/useSchoolRoster';
import type { SchoolResults } from '../../hooks/useSchoolResults';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { classLabel } from './ClassDrawer';

type Tab = 'overview' | 'classes' | 'students' | 'teachers';
type Go = (next: { tab: Tab; open?: string | null; filter?: string | null }) => void;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const lessonsIn = (list: Result[]) => new Set(list.map((r) => r.sessionId).filter(Boolean)).size;

export function OverviewTab({ roster, results, onGo }: { roster: SchoolRoster; results: SchoolResults; onGo: Go }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<RangeValue>(() => makeRange('30d'));

  const inPeriod = results.results.filter((r) => inRange(r, period.range));
  const prev = previousRange(period.range);
  const inPrevious = prev ? results.results.filter((r) => inRange(r, prev)) : [];

  const summary = summarise(inPeriod, results.passThreshold);
  const prevSummary = summarise(inPrevious, results.passThreshold);
  const enrolled = roster.students.filter((s) => s.classIds.some((id) => roster.classById.has(id))).length;
  const approved = roster.teachers.filter((t) => t.approvalStatus === 'approved').length;
  const pending = roster.teachers.filter((t) => (t.approvalStatus ?? 'pending') === 'pending').length;
  const noLead = roster.classes.filter((c) => !c.class_teacher_id).length;

  const items = useMemo(
    () =>
      needsAttention({
        classes: roster.classes,
        teachers: roster.teachers,
        students: roster.students,
        // Without results every staffed class would read as quiet; say nothing instead.
        results: results.error ? [] : results.results,
      }).filter((i) => !(results.error && (i.kind === 'quiet_classes' || i.kind === 'quiet_teachers'))),
    [roster.classes, roster.teachers, roster.students, results.results, results.error]
  );

  const activity = useMemo(
    () => classActivity(roster.classes.map((c) => c.id), results.results, period.range, results.passThreshold),
    [roster.classes, results.results, period, results.passThreshold]
  );

  const loading = roster.loading;
  const figuresLoading = loading || results.loading;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Activity figures cover the selected period.</p>
        <DateRangeFilter value={period} onChange={setPeriod} />
      </div>

      <MetricGrid>
        <MetricCard loading={loading} label="Teachers" value={String(approved)} detail={pending ? `${pending} awaiting approval` : 'all decided'} definition="Approved teachers at the school." />
        <MetricCard loading={loading} label="Classes" value={String(roster.classes.length)} detail={noLead ? `${noLead} without a class teacher` : 'all have a class teacher'} definition="Active (not archived) classes." />
        <MetricCard
          loading={loading}
          label="Students placed"
          value={`${enrolled}/${roster.students.length}`}
          detail={roster.students.length - enrolled ? `${roster.students.length - enrolled} in no class` : 'everyone is in a class'}
          definition="Students in at least one active class, out of all students at the school."
        />
        <MetricCard
          loading={figuresLoading}
          label="Lessons conducted"
          value={results.error ? '—' : String(lessonsIn(inPeriod))}
          detail={prev && !results.error ? `${lessonsIn(inPrevious)} in the previous period` : undefined}
          definition="Live-class sessions that produced quiz results in the period."
        />
        <MetricCard
          loading={figuresLoading}
          label="Active students"
          value={results.error ? '—' : String(summary.students)}
          detail={roster.students.length && !results.error ? `${formatPercent((summary.students / roster.students.length) * 100)} of students` : undefined}
          definition="Students with at least one quiz result in the period."
        />
        <MetricCard
          loading={figuresLoading}
          label="Average score"
          value={formatPercent(summary.average)}
          deltaPp={summary.average !== null && prevSummary.average !== null ? summary.average - prevSummary.average : null}
          definition="Mean quiz percentage across submissions in the period."
        />
      </MetricGrid>

      <div className="grid gap-5 lg:grid-cols-5">
        <AnalyticsPanel title="Needs attention" description="Most pressing first." className="lg:col-span-2">
          {loading ? (
            <TableSkeleton rows={4} />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <CheckCircle2 className="mb-2 h-7 w-7 text-primary" aria-hidden />
              <p className="text-sm font-medium text-foreground">Nothing needs attention</p>
              <p className="mt-1 text-xs text-muted-foreground">Every teacher and student is decided, every class staffed and every student placed.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <AttentionRow key={item.kind} item={item} roster={roster} onGo={onGo} />
              ))}
            </ul>
          )}
        </AnalyticsPanel>

        <AnalyticsPanel title="Average score by class" description="Select a bar to open the class’s performance." className="lg:col-span-3">
          {figuresLoading ? (
            <ChartSkeleton />
          ) : results.error ? (
            <AnalyticsErrorState message={results.error} />
          ) : [...activity.values()].every((a) => a.average === null) ? (
            <AnalyticsEmptyState compact title="No quiz results in this period" description="Averages appear once classes complete a lesson quiz." />
          ) : (
            <ComparisonChart
              valueLabel="Average score"
              data={roster.classes.map((c) => {
                const a = activity.get(c.id);
                return { key: c.id, label: classLabel(c), value: a?.average ?? null, note: a ? `${plural(a.activeStudents, 'student')} · ${plural(a.lessons, 'lesson')}` : undefined };
              })}
              onSelect={(id) => navigate(`/analytics/classes/${id}`)}
            />
          )}
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel title="Class activity" description="Per class, for the selected period. Last activity is all-time.">
        {loading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={roster.classes}
            columns={activityColumns(roster, activity, results)}
            rowKey={(c) => c.id}
            initialSort={{ key: 'last', direction: 'asc' }}
            onRowClick={(c) => onGo({ tab: 'classes', open: c.id })}
            empty={<AnalyticsEmptyState compact title="No classes yet" description="Create classes in the Classes tab." />}
          />
        )}
        {results.truncated && <AnalyticsNote>Only the most recent results are included; older activity is not counted.</AnalyticsNote>}
        <AnalyticsNote>Activity is read from quiz results, so a lesson taught without a quiz does not appear here.</AnalyticsNote>
      </AnalyticsPanel>
    </div>
  );
}

function activityColumns(
  roster: SchoolRoster,
  activity: ReturnType<typeof classActivity>,
  results: SchoolResults
): Column<SchoolRoster['classes'][number]>[] {
  const days = (ms: number | null) => (ms === null ? null : Math.floor((Date.now() - ms) / 86_400_000));
  return [
    {
      key: 'name',
      header: 'Class',
      sortValue: (c) => classLabel(c),
      cell: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{classLabel(c)}</p>
          <p className="truncate text-xs text-muted-foreground">
            {c.class_teacher_id ? roster.teacherById.get(c.class_teacher_id)?.name ?? '—' : 'No class teacher'}
          </p>
        </div>
      ),
    },
    {
      key: 'students',
      header: 'Students',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => (c.student_ids ?? []).length,
      cell: (c) => `${(c.student_ids ?? []).length}/${MAX_CLASS_SIZE}`,
    },
    {
      key: 'lessons',
      header: 'Lessons',
      align: 'right',
      sortValue: (c) => activity.get(c.id)?.lessons ?? 0,
      cell: (c) => (results.error ? '—' : activity.get(c.id)?.lessons ?? 0),
    },
    {
      key: 'active',
      header: 'Active students',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => activity.get(c.id)?.activeStudents ?? 0,
      cell: (c) => (results.error ? '—' : activity.get(c.id)?.activeStudents ?? 0),
    },
    {
      key: 'average',
      header: 'Average',
      align: 'right',
      sortValue: (c) => activity.get(c.id)?.average ?? null,
      cell: (c) => {
        const avg = activity.get(c.id)?.average ?? null;
        return <span className={scoreTone(avg, results.passThreshold)}>{formatPercent(avg)}</span>;
      },
    },
    {
      key: 'last',
      header: 'Last activity',
      align: 'right',
      // Longest-quiet first by default: null (never) sorts last, so invert to days.
      sortValue: (c) => {
        const d = days(activity.get(c.id)?.lastActivity ?? null);
        return d === null ? null : -d;
      },
      cell: (c) => {
        const d = days(activity.get(c.id)?.lastActivity ?? null);
        if (results.error) return '—';
        if (d === null) return <span className="text-muted-foreground">Never</span>;
        const tone = d >= QUIET_DAYS ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';
        return <span className={tone}>{d === 0 ? 'Today' : `${d}d ago`}</span>;
      },
    },
  ];
}

function AttentionRow({ item, roster, onGo }: { item: AttentionItem; roster: SchoolRoster; onGo: Go }) {
  const n = item.ids.length;
  const one = n === 1 ? item.ids[0] : null;
  const names = (ids: string[], nameOf: (id: string) => string | undefined) => {
    const list = ids.map(nameOf).filter(Boolean) as string[];
    return list.length > 3 ? `${list.slice(0, 3).join(', ')} and ${list.length - 3} more` : list.join(', ');
  };
  const teacherNames = () => names(item.ids, (id) => roster.teacherById.get(id)?.name);
  const classNames = () => names(item.ids, (id) => {
    const c = roster.classById.get(id);
    return c ? classLabel(c) : undefined;
  });

  const content: Record<AttentionItem['kind'], { title: string; detail: string; go: Parameters<Go>[0]; action: string }> = {
    pending_teachers: {
      title: `${plural(n, 'teacher')} waiting for approval`,
      detail: teacherNames(),
      go: { tab: 'teachers', filter: 'pending', open: one },
      action: 'Review',
    },
    students_awaiting_school: {
      title: `${plural(n, 'student')} waiting for the school to approve`,
      detail: 'They asked for no class, or for a class with no teacher, so no class teacher can approve them.',
      go: { tab: 'students', filter: 'pending', open: one },
      action: 'Review',
    },
    no_class_teacher: {
      title: `${plural(n, 'class', 'classes')} without a class teacher`,
      detail: `No one can approve their students. ${classNames()}`,
      go: { tab: 'classes', open: one },
      action: 'Assign',
    },
    unplaced_students: {
      title: `${plural(n, 'approved student')} in no class`,
      detail: 'They cannot join a live class until they are placed.',
      go: { tab: 'students', filter: 'unplaced' },
      action: 'Place',
    },
    near_full: {
      title: `${plural(n, 'class', 'classes')} nearly full`,
      detail: `${NEAR_FULL_AT}+ of ${MAX_CLASS_SIZE} seats taken. ${classNames()}`,
      go: { tab: 'classes', open: one },
      action: 'View',
    },
    teachers_without_classes: {
      title: `${plural(n, 'approved teacher')} with no classes`,
      detail: teacherNames(),
      go: { tab: 'teachers', filter: 'approved', open: one },
      action: 'Assign',
    },
    quiet_teachers: {
      title: `${plural(n, 'teacher')} with no quiz results in ${QUIET_DAYS} days`,
      detail: teacherNames(),
      go: { tab: 'teachers', filter: 'approved', open: one },
      action: 'View',
    },
    quiet_classes: {
      title: `${plural(n, 'class', 'classes')} with no quiz results in ${QUIET_DAYS} days`,
      detail: classNames(),
      go: { tab: 'classes', open: one },
      action: 'View',
    },
  };
  const c = content[item.kind];
  const Icon = item.severity === 'act' ? AlertCircle : Eye;
  return (
    <li className="flex items-start gap-3 py-2.5">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${item.severity === 'act' ? 'text-amber-500' : 'text-muted-foreground'}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{c.title}</p>
        {c.detail && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.detail}</p>}
      </div>
      <Button size="sm" variant="ghost" className="shrink-0" onClick={() => onGo(c.go)}>
        {c.action} <ArrowRight className="ml-1 h-3.5 w-3.5" />
      </Button>
    </li>
  );
}
